import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import { useAuth } from '../../context/AuthContext';
import { PendingUsersBanner } from '../../components/PendingUsersBanner';
import { getRooms, Room, getProjects, Project } from '../../api/client';

export function BulkQRCodes() {
  const { projectId } = useParams<{ projectId: string }>();
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [qrSizeInches, setQrSizeInches] = useState(0.75); // QR code size in inches (0.5-2.5)
  const [qrBorderInches, setQrBorderInches] = useState(0.1); // Border/padding in inches (0-0.25)
  const [gridSpacingInches, setGridSpacingInches] = useState(0.2); // Space between QR codes on sheet (0-0.5)

  const getDisplayNumber = (room: Room) => (room.is_exterior ? `EXT-${room.number}` : room.number);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }

    const loadData = async () => {
      if (!token || !projectId) return;
      try {
        const [projects, roomsData] = await Promise.all([
          getProjects(token),
          getRooms(token, parseInt(projectId))
        ]);

        const foundProject = projects.find(p => p.id === parseInt(projectId));
        if (!foundProject) {
          console.error('Project not found');
          navigate('/admin');
          return;
        }

        setProject(foundProject);
        setRooms(roomsData);
      } catch (error) {
        console.error('Failed to load data:', error);
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, token, isAuthenticated, navigate]);

  const svgToDataURL = (svg: SVGElement, width: number, height: number, border: number = 0): Promise<string> => {
    return new Promise((resolve) => {
      const svgData = new XMLSerializer().serializeToString(svg);
      const totalWidth = width + border * 2;
      const totalHeight = height + border * 2;
      const canvas = document.createElement('canvas');
      canvas.width = totalWidth;
      canvas.height = totalHeight;
      const ctx = canvas.getContext('2d');

      const img = new Image();
      img.onload = () => {
        if (ctx) {
          // Fill entire canvas with white (this creates the border)
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, totalWidth, totalHeight);
          // Draw QR code offset by border amount
          ctx.drawImage(img, border, border, width, height);
        }
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => {
        resolve('');
      };
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    });
  };

  const handleDownloadQR = async (roomId: number, roomNumber: string) => {
    const qrElements = document.querySelectorAll(`[data-room-id="${roomId}"] svg`);
    if (qrElements.length === 0) return;

    const svg = qrElements[0] as SVGElement;
    // Use higher DPI for download (300 DPI)
    const downloadSize = Math.round(qrSizeInches * 300);
    const downloadBorder = Math.round(qrBorderInches * 300);
    const imgData = await svgToDataURL(svg, downloadSize, downloadSize, downloadBorder);

    const downloadLink = document.createElement('a');
    downloadLink.download = `room-${roomNumber}-qr.png`;
    downloadLink.href = imgData;
    downloadLink.click();
  };

  const generateLabelSheet = async () => {
    if (rooms.length === 0) {
      alert('No rooms to generate QR codes for');
      return;
    }

    setGenerating(true);

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: 'letter'
      });

      const pageWidth = 8.5;
      const pageHeight = 11;
      const pageMargin = 0.5; // Half inch margin on all sides
      const gutterX = gridSpacingInches; // Space between columns
      const gutterY = gridSpacingInches; // Space between rows

      // Total size of each label (QR + border + text space)
      const totalQrSize = qrSizeInches + (qrBorderInches * 2);
      const textHeight = 0.3; // Space for room number and name text
      const labelWidth = totalQrSize;
      const labelHeight = totalQrSize + textHeight;

      // Calculate how many columns and rows fit on a page
      const usableWidth = pageWidth - (pageMargin * 2);
      const usableHeight = pageHeight - (pageMargin * 2);

      const cols = Math.max(1, Math.floor((usableWidth + gutterX) / (labelWidth + gutterX)));
      const rows = Math.max(1, Math.floor((usableHeight + gutterY) / (labelHeight + gutterY)));
      const labelsPerPage = cols * rows;

      // Center the grid on the page
      const totalGridWidth = cols * labelWidth + (cols - 1) * gutterX;
      const totalGridHeight = rows * labelHeight + (rows - 1) * gutterY;
      const marginLeft = (pageWidth - totalGridWidth) / 2;
      const marginTop = (pageHeight - totalGridHeight) / 2;

      // Convert inches to pixels for rendering (150 DPI for good quality)
      const dpi = 150;
      const qrSizePixels = Math.round(qrSizeInches * dpi);
      const borderPixels = Math.round(qrBorderInches * dpi);

      // Font size scales with QR code size
      const fontSize = Math.max(6, Math.min(12, qrSizeInches * 10));

      // Generate all QR code images first
      const qrImages: string[] = [];
      for (let i = 0; i < rooms.length; i++) {
        const room = rooms[i];

        // Get QR code from rendered element
        const qrElements = document.querySelectorAll(`[data-room-id="${room.id}"] svg`);
        if (qrElements.length > 0) {
          const svg = qrElements[0] as SVGElement;
          const imgData = await svgToDataURL(svg, qrSizePixels, qrSizePixels, borderPixels);
          qrImages.push(imgData);
        } else {
          qrImages.push('');
        }
      }

      // Now add all labels to PDF
      for (let i = 0; i < rooms.length; i++) {
        const room = rooms[i];
        const labelIndex = i % labelsPerPage;

        // Add new page if needed (but not on first label)
        if (i > 0 && labelIndex === 0) {
          pdf.addPage();
        }

        const col = labelIndex % cols;
        const row = Math.floor(labelIndex / cols);

        const x = marginLeft + col * (labelWidth + gutterX);
        const y = marginTop + row * (labelHeight + gutterY);

        // Add QR code image if available
        if (qrImages[i]) {
          // Center QR code in label cell
          const qrX = x + (labelWidth - totalQrSize) / 2;
          const qrY = y;
          pdf.addImage(qrImages[i], 'PNG', qrX, qrY, totalQrSize, totalQrSize);
        }

        // Add room number and name text below QR code
        const textY = y + totalQrSize + 0.08;
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', 'bold');

        const roomText = `Room ${getDisplayNumber(room)}`;
        const labelCenterX = x + labelWidth / 2;

        pdf.text(roomText, labelCenterX, textY, { align: 'center' });

        if (room.name) {
          pdf.setFontSize(fontSize * 0.85);
          pdf.setFont('helvetica', 'normal');
          pdf.text(room.name, labelCenterX, textY + fontSize / 72 + 0.02, { align: 'center' });
        }
      }

      // Save PDF
      pdf.save(`${project?.name || 'project'}-qr-labels.pdf`);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ fontFamily: 'system-ui', padding: '24px' }}>
        Loading...
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div style={{ fontFamily: 'system-ui' }}>
      <header style={{
        padding: '16px 24px',
        background: '#1a1a2e',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(`/admin/projects/${projectId}`)}
          style={{
            background: 'transparent',
            border: '1px solid #fff',
            color: '#fff',
            padding: '8px 16px',
            cursor: 'pointer',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          ← Back to Project
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px' }}>
            QR Code Label Sheet
          </h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
            {project.name}
          </p>
        </div>
      </header>

      <PendingUsersBanner />

      <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{
          background: '#f8f9fa',
          padding: '24px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <h2 style={{ marginTop: 0 }}>QR Code Settings</h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px',
            marginBottom: '24px'
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                QR Code Size: {qrSizeInches.toFixed(2)}"
              </label>
              <input
                type="range"
                min="0.5"
                max="2.5"
                step="0.05"
                value={qrSizeInches}
                onChange={(e) => setQrSizeInches(parseFloat(e.target.value))}
                style={{ width: '100%', cursor: 'pointer' }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                color: '#666',
                marginTop: '4px'
              }}>
                <span>0.5"</span>
                <span>2.5"</span>
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                QR Border: {qrBorderInches.toFixed(2)}"
              </label>
              <input
                type="range"
                min="0"
                max="0.25"
                step="0.01"
                value={qrBorderInches}
                onChange={(e) => setQrBorderInches(parseFloat(e.target.value))}
                style={{ width: '100%', cursor: 'pointer' }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                color: '#666',
                marginTop: '4px'
              }}>
                <span>0"</span>
                <span>0.25"</span>
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                Grid Spacing: {gridSpacingInches.toFixed(2)}"
              </label>
              <input
                type="range"
                min="0"
                max="0.5"
                step="0.02"
                value={gridSpacingInches}
                onChange={(e) => setGridSpacingInches(parseFloat(e.target.value))}
                style={{ width: '100%', cursor: 'pointer' }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                color: '#666',
                marginTop: '4px'
              }}>
                <span>0"</span>
                <span>0.5"</span>
              </div>
            </div>
          </div>

          {/* Layout Preview */}
          {(() => {
            const totalSize = qrSizeInches + (qrBorderInches * 2) + 0.3; // Include text space
            const usableWidth = 8.5 - 1; // Page width minus margins
            const usableHeight = 11 - 1; // Page height minus margins
            const cols = Math.max(1, Math.floor((usableWidth + gridSpacingInches) / (totalSize + gridSpacingInches)));
            const rows = Math.max(1, Math.floor((usableHeight + gridSpacingInches) / (totalSize + gridSpacingInches)));
            const labelsPerPage = cols * rows;

            return (
              <div style={{
                background: '#e9ecef',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <strong>Layout Preview:</strong> {cols} columns × {rows} rows = {labelsPerPage} labels per page
                {labelsPerPage < rooms.length && (
                  <span style={{ color: '#666' }}> ({Math.ceil(rooms.length / labelsPerPage)} pages needed)</span>
                )}
              </div>
            );
          })()}

          <h3 style={{ marginTop: '24px', marginBottom: '12px' }}>Generate Label Sheet</h3>
          <p style={{ color: '#666', marginBottom: '16px' }}>
            Generate a PDF with QR codes for all {rooms.length} rooms.
            Layout adjusts automatically based on QR code size.
          </p>
          <button
            onClick={generateLabelSheet}
            disabled={generating || rooms.length === 0}
            style={{
              background: generating ? '#6c757d' : '#007bff',
              color: '#fff',
              border: 'none',
              padding: '12px 24px',
              cursor: generating || rooms.length === 0 ? 'not-allowed' : 'pointer',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {generating ? 'Generating PDF...' : 'Download All as Label Sheet'}
          </button>
        </div>

        <div>
          <h2>QR Code Preview</h2>
          <p style={{ color: '#666', marginBottom: '16px' }}>
            Preview of QR codes that will be included in the label sheet:
          </p>

          {rooms.length === 0 ? (
            <p style={{ color: '#666' }}>No rooms in this project yet.</p>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              {rooms.map(room => {
                const qrUrl = `${window.location.origin}/${project.slug}/${getDisplayNumber(room)}`;
                // Convert inches to pixels for preview (96 DPI screen)
                const previewSize = Math.round(qrSizeInches * 96);
                const previewBorder = Math.round(qrBorderInches * 96);
                return (
                  <div
                    key={room.id}
                    data-room-id={room.id}
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '16px',
                      background: '#fff',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                  >
                    <div style={{
                      padding: `${previewBorder}px`,
                      background: '#fff',
                      display: 'inline-block'
                    }}>
                      <QRCodeSVG value={qrUrl} size={previewSize} level="H" />
                    </div>
                    <div style={{ textAlign: 'center', width: '100%' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                        Room {getDisplayNumber(room)}
                      </div>
                      {room.name && (
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                          {room.name}
                        </div>
                      )}
                      <button
                        onClick={() => handleDownloadQR(room.id, getDisplayNumber(room))}
                        style={{
                          background: '#28a745',
                          color: '#fff',
                          border: 'none',
                          padding: '6px 12px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          fontSize: '12px',
                          marginTop: '4px'
                        }}
                      >
                        Download
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
